<template>
  <div>
    <v-row class="mb-2 mt-2">
      <v-col
        cols="12"
        sm="5"
        class="order-last order-sm-first"
      >
        <v-text-field
          v-model="filterQuery"
          hide-details
          clearable
          :label="$t('Search')"
        />
      </v-col>

      <v-col
        v-if="faq.categories.length > 0"
        cols="12"
        sm="7"
        class="text-right order-first order-sm-last align-end d-flex justify-end"
      >
        <v-btn
          x-small
          elevation="1"
          class="mr-1"
          @click="setExpandAll(true)"
        >
          {{ $t('Expand all') }}
        </v-btn>
        <v-btn
          x-small
          elevation="1"
          class="ml-1"
          @click="setExpandAll(false)"
        >
          {{ $t('Minimize all') }}
        </v-btn>
      </v-col>
    </v-row>

    <v-list
      expand
      class="manage-list"
    >
      <v-list-group
        v-for="(category, catIndex) in categories"
        :key="`category-${category.id}`"
        v-model="openState[category.id]"
        :ripple="false"
        prepend-icon=""
        no-action
      >
        <template #activator>
          <manage-faq-list-category-item
            :filter-active="filterActive"
            :category="category"
            :category-index="catIndex"
            @show-options-menu="showOptionsMenu"
            @move-item-up="moveItemUp"
            @move-item-down="moveItemDown"
          />
        </template>

        <v-list-group
          v-for="(subcategory, subcatIndex) in category.subcategories"
          :key="`subcategory-${subcategory.id}`"
          v-model="openState[`${category.id}_${subcategory.id}`]"
          :ripple="false"
          no-action
          sub-group
        >
          <template #activator>
            <manage-faq-list-category-item
              :filter-active="filterActive"
              :category="subcategory"
              :category-index="catIndex"
              :subcategory-index="subcatIndex"
              :is-subcategory="true"
              @show-options-menu="showOptionsMenu"
              @move-item-up="moveItemUp"
              @move-item-down="moveItemDown"
            />
          </template>

          <manage-faq-list-question-item
            v-for="(question, questionIndex) in subcategory.questions"
            :key="`subcategory-question-${question.id}`"
            :filter-active="filterActive"
            :question="question"
            :question-index="questionIndex"
            :category-index="catIndex"
            :subcategory-index="subcatIndex"
            @click="editQuestion(question)"
            @show-options-menu="showOptionsMenu"
            @move-item-up="moveItemUp"
            @move-item-down="moveItemDown"
          />
        </v-list-group>

        <manage-faq-list-question-item
          v-for="(question, questionIndex) in category.questions"
          :key="`category-question-${question.id}`"
          :filter-active="filterActive"
          dense
          :question="question"
          :question-index="questionIndex"
          :category-index="catIndex"
          @show-options-menu="showOptionsMenu"
          @move-item-up="moveItemUp"
          @move-item-down="moveItemDown"
          @click="editQuestion(question)"
        />
      </v-list-group>
    </v-list>

    <options-menu
      v-bind="optionsMenu"
      @remove-category="removeCategory"
      @create-subcategory="createSubcategory"
      @remove-subcategory="removeSubcategory"
      @edit-category="editCategory"
      @edit-subcategory="editSubcategory"
      @create-question="createQuestion"
      @edit-question="editQuestion"
      @remove-question="removeQuestion"
    />
  </div>
</template>

<script>
import { router } from '@inertiajs/vue2';
import debounce from 'lodash/debounce';
import cloneDeep from 'lodash/cloneDeep';
import OptionsMenu from '@/components/faq/manage/OptionsMenu';
import ManageFaqListQuestionItem from '@/components/faq/manage/ManageFaqListQuestionItem';
import ManageFaqListCategoryItem from '@/components/faq/manage/ManageFaqListCategoryItem';

const AUTO_EXPAND_CATEGORIES_IF_NUM_QUESTIONS_LT = 100;
const AUTO_EXPAND_CATEGORIES_IF_NUM_CATEGORIES_LT = 3;
const FILTER_EXPAND_CATEGORY_IF_QUESTION_COUNT_LTE = 5;
const FILTER_EXPAND_CATEGORIES_IF_COUNT_LTE = 3;

export default {
  components: {
    ManageFaqListQuestionItem,
    ManageFaqListCategoryItem,
    OptionsMenu
  },

  props: {
    faq: {
      type: Object,
      required: true
    }
  },

  remember: 'openState',

  data() {
    return {
      openState: {},
      filterQuery: '',
      filterActive: false,
      categories: [],
      optionsMenu: {
        x: 0,
        y: 0,
        show: false,
        type: '',
        item: {}
      }
    }
  },

  watch: {
    filterQuery: function(value) {
      if (value === null || (value !== null && value.length === 0)) {
        this.setExpandAll(false);
        this.categories = this.faq.categories;
        this.maybeOpenCategories()
        this.filterActive = false;
        return;
      }

      if (value !== null && value.length >= 3) {
        this.filter(value);
      }
    }
  },

  beforeDestroy() {
    router.remember(this.openState, 'openState')
  },

  mounted() {
    const openState = router.restore('openState');

    if (openState) {
      this.openState = openState;
    }
  },

  created() {
    this.prepareOpenState();

    this.categories = this.faq.categories;

    this.maybeOpenCategories();

    const debounceMs = 500;
    this.updateCategorySortOrder = debounce(this.updateCategorySortOrder, debounceMs);
    this.updateCategoryQuestionSortOrder = debounce(this.updateCategoryQuestionSortOrder, debounceMs);
    this.updateSubcategorySortOrder = debounce(this.updateSubcategorySortOrder, debounceMs);
    this.updateSubcategoryQuestionSortOrder = debounce(this.updateSubcategoryQuestionSortOrder, debounceMs);
    this.filter = debounce(this.filter, 1000);
  },

  methods: {
    filter(query) {
      let categories = cloneDeep(this.faq.categories);

      const openState = {};

      categories = categories.map((category) => {
        category.questions = category.questions.filter((question) => {
          const title = question.default_translation.title.toLowerCase();

          return title.includes(query.toLowerCase());
        });

        if (category.questions.length <= FILTER_EXPAND_CATEGORY_IF_QUESTION_COUNT_LTE) {
          openState[category.id] = true;
        }

        category.subcategories = category.subcategories.map((subcategory) => {
          subcategory.questions = subcategory.questions.filter((question) => {
            const title = question.default_translation.title.toLowerCase();

            return title.includes(query.toLowerCase());
          });

          if (subcategory.questions.length <= FILTER_EXPAND_CATEGORY_IF_QUESTION_COUNT_LTE) {
            openState[`${category.id}_${subcategory.id}`] = true;
          }

          return subcategory;
        }).filter((subcategory) => subcategory.questions.length);

        return category;
      });

      this.categories = categories.filter((category) => {
        return category.questions.length > 0 || category.subcategories.length > 0;
      });

      if (this.categories.length <= FILTER_EXPAND_CATEGORIES_IF_COUNT_LTE) {
        this.openState = {
          ...this.openState,
          ...openState
        };
      }

      this.filterActive = true;
    },

    maybeOpenCategories() {
      if (
        this.faq.num_questions < AUTO_EXPAND_CATEGORIES_IF_NUM_QUESTIONS_LT &&
        this.categories.length < AUTO_EXPAND_CATEGORIES_IF_NUM_CATEGORIES_LT
      ) {
        for (const key in this.openState) {
          const isSubcategory = key.includes('_');
          if (! isSubcategory) {
            this.openState[key] = true;
          } else {
            this.openState[key] = false;
          }
        }
      }
    },

    prepareOpenState() {
      this.faq.categories.forEach(category => {
        this.$set(this.openState, category.id, false);
        category.subcategories.forEach(subcategory => {
          this.$set(this.openState, `${category.id}_${subcategory.id}`, false);
        });
      });
    },

    setExpandAll(value) {
      for (const key in this.openState) {
        this.openState[key] = value;
      }
    },

    inertiaRequestOptions(overrides = {}) {
      return {
        preserveScroll: true,
        onSuccess: () => this.$root.refreshFaqPreview(),
        ...overrides
      }
    },

    updateCategorySortOrder() {
      const order = this.categories.filter(cat => !cat.uncategorized).map(cat => cat.id);
      const url = this.route('faq.category.sort-order.update', this.faq.id);
      this.$inertia.patch(url, { order }, this.inertiaRequestOptions());
    },

    updateCategoryQuestionSortOrder(catIdx) {
      const category = this.categories[catIdx];
      const url = this.route('faq.category.questions.sort-order.update', [this.faq.id, category.id]);
      const order = category.questions.map(obj => obj.id);
      this.$inertia.patch(url, { order }, this.inertiaRequestOptions());
    },

    updateSubcategorySortOrder(catIdx) {
      const order = this.categories[catIdx].subcategories.map(obj => obj.id);
      const url = this.route('faq.subcategory.sort-order.update', this.faq.id);
      this.$inertia.patch(url, { order }, this.inertiaRequestOptions());
    },

    updateSubcategoryQuestionSortOrder(catIdx, subcatIdx) {
      const subcategory = this.categories[catIdx].subcategories[subcatIdx];
      const order = subcategory.questions.map(obj => obj.id);
      const url = this.route('faq.subcategory.questions.sort-order.update', [this.faq.id, subcategory.id]);
      this.$inertia.patch(url, { order }, this.inertiaRequestOptions());
    },

    moveCategory(fromIdx, toIdx) {
      this.categories.splice(toIdx, 0, this.categories.splice(fromIdx, 1)[0]);
    },

    moveCategoryQuestion(catIdx, fromIdx, toIdx) {
      this.categories[catIdx].questions.splice(
        toIdx, 0, this.categories[catIdx].questions.splice(fromIdx, 1)[0]
      );
    },

    moveSubcategory(catIdx, fromIdx, toIdx) {
      this.categories[catIdx].subcategories.splice(
        toIdx, 0, this.categories[catIdx].subcategories.splice(fromIdx, 1)[0]
      );
    },

    moveSubcategoryQuestion(catIdx, subcatIdx, fromIdx, toIdx) {
      this.categories[catIdx].subcategories[subcatIdx].questions.splice(
        toIdx, 0, this.categories[catIdx].subcategories[subcatIdx].questions.splice(fromIdx, 1)[0]
      );
    },

    moveItemUp({ type, catIdx, subcatIdx, quesIdx }) {
      const subcategories = this.categories[catIdx].subcategories;

      if (type === 'category') {

        if (this.categories[catIdx - 1] && this.categories[catIdx - 1].uncategorized) {
          this.moveCategory(1, this.categories.length - 1);
        } else {
          if (catIdx === 0 || (this.categories[catIdx] && this.categories[catIdx].uncategorized)) {
            this.moveCategory(0, this.categories.length - 1);
          } else {
            this.moveCategory(catIdx, catIdx - 1);
          }
        }

        this.updateCategorySortOrder();

      } else if (type === 'category-question') {

        if (this.categories[catIdx].questions[quesIdx - 1] === undefined) {
          this.moveCategoryQuestion(catIdx, quesIdx, this.categories[catIdx].questions.length - 1)
        } else {
          this.moveCategoryQuestion(catIdx, quesIdx, quesIdx - 1);
        }

        this.updateCategoryQuestionSortOrder(catIdx);

      }  else if (type === 'subcategory') {

        if (subcatIdx === 0) {
          this.moveSubcategory(catIdx, 0, subcategories.length - 1);
        } else {
          this.moveSubcategory(catIdx, subcatIdx, subcatIdx - 1);
        }

        this.updateSubcategorySortOrder(catIdx);

      } else if (type === 'subcategory-question') {

        if (quesIdx === 0) {
          this.moveSubcategoryQuestion(
            catIdx, subcatIdx, 0, this.categories[catIdx].subcategories[subcatIdx].questions.length - 1
          );
        } else {
          this.moveSubcategoryQuestion(catIdx, subcatIdx, quesIdx, quesIdx - 1);
        }

        this.updateSubcategoryQuestionSortOrder(catIdx, subcatIdx);

      }
    },

    moveItemDown({ type, catIdx, subcatIdx, quesIdx }) {
      const subcategories = this.categories[catIdx].subcategories;

      if (type === 'category') {

        if (catIdx === this.categories.length - 1 && (this.categories[0] && this.categories[0].uncategorized)) {
          this.moveCategory(this.categories.length - 1, 1);
        } else {
            if (catIdx === this.categories.length - 1) {
            this.moveCategory(this.categories.length - 1, 0);
          } else {
            this.moveCategory(catIdx, catIdx + 1);
          }
        }

        this.updateCategorySortOrder();

      } else if (type === 'category-question') {

        if (quesIdx === this.categories[catIdx].questions.length - 1) {
          this.moveCategoryQuestion(catIdx, quesIdx, subcategories.length);
        } else {
          this.moveCategoryQuestion(catIdx, quesIdx, quesIdx + 1);
        }

        this.updateCategoryQuestionSortOrder(catIdx);

      } else if (type === 'subcategory') {

        if (subcategories[subcatIdx + 1] === undefined) {
          this.moveSubcategory(catIdx, subcatIdx, 0);
        } else {
          this.moveSubcategory(catIdx, subcatIdx, subcatIdx + 1);
        }

        this.updateSubcategorySortOrder(catIdx);

      } else if (type === 'subcategory-question') {

        if (this.categories[catIdx].subcategories[subcatIdx].questions.length - 1) {
          this.moveSubcategoryQuestion(
            catIdx, subcatIdx, this.categories[catIdx].subcategories[subcatIdx].questions.length - 1, 0
          );
        } else {
          this.moveSubcategoryQuestion(catIdx, subcatIdx, quesIdx, quesIdx + 1);
        }

        this.updateSubcategoryQuestionSortOrder(catIdx, subcatIdx);

      }
    },

    showOptionsMenu(type, item, toggleBtn) {
      this.optionsMenu.show = false;
      this.optionsMenu.item = item;
      this.optionsMenu.type = type;

      const rect = toggleBtn.getBoundingClientRect();
      this.optionsMenu.x = rect.left;
      this.optionsMenu.y = rect.top + toggleBtn.clientHeight;

      setTimeout(() => {
        this.optionsMenu.show = true;
      }, 50);
    },

    editCategory(category) {
      this.$emit('edit-category', category);
    },

    createSubcategory(category) {
      this.$emit('create-subcategory', category);
    },

    editSubcategory(subcategory) {
      this.$emit('edit-subcategory', subcategory);
    },

    async removeSubcategory(subcategory) {
      const confirmed = await this.$root.$confirm(
          this.$t(`Remove subcategory`),
          this.$t(`Are you sure you want to remove the subcategory '{subcategory}'? The questions in this category will also be removed.`,
            { subcategory: subcategory.default_translation.name }
          )
        );

        if (confirmed) {
          const url = this.route('faq.subcategory.destroy', [this.faq.id, subcategory.id]);

          this.$inertia.delete(url, this.inertiaRequestOptions({
            preserveState: false
          }));
        }
    },

    async removeCategory(category) {
      const confirmed = await this.$root.$confirm(
        this.$t(`Remove category`),
        this.$t(`Are you sure you want to remove the category {category}? The subcategories and questions in this category will also be removed.`, { category: category.default_translation.name })
      );

      if (confirmed) {
        const url = this.route('faq.category.destroy', [this.faq.id, category.id]);

        this.$inertia.delete(url, this.inertiaRequestOptions({
          preserveState: false
        }));
      }
    },

    createQuestion(item) {
      const queryParams = {
        faq: this.faq.id,
      };

      const isSubcategory = item.category_id !== undefined;

      if (isSubcategory) {
        queryParams.category = item.category_id;
        queryParams.subcategory = item.id;
      } else {
        queryParams.category = item.id;
      }

      const url = this.route('faq.create-question', queryParams);

      this.$inertia.visit(url);
    },

    editQuestion(question) {
      const url = this.route('faq.question.edit', [this.faq.id, question.id]);

      router.remember(this.openState, 'openState');

      this.$inertia.visit(url, {}, {
        preserveState: true,
        preserveScroll: false,
      });
    },

    async removeQuestion(question) {
      const confirmed = await this.$root.$confirm(
        this.$t(`Remove question`),
        this.$t(`Are you sure you want to remove the question '{question}'?`, { question: question.default_translation.title })
      );

      if (confirmed) {
        const url = this.route('faq.question.destroy', [this.faq.id, question.id]);
        this.$inertia.delete(url, this.inertiaRequestOptions({
          preserveState: false,
        }));
      }
    }
  },
}
</script>

<style>
  .manage-list .v-list-item--active .v-list-item__icon {
    color: var(--v-primary-base) !important;
  }
</style>